import { deliveryRetryPlan, deliveryRetryFields, type DeliveryRetryPlan } from '../lib/deliveryRetry.js';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ZodTypeAny } from 'zod';
import { sendQuoteSchema, sendSignatureSchema, type Quote, type SignatureDocument } from '@worki/shared';
import { api } from '../lib/api.js';
import * as platform from '@platform';
import { sendActionOf } from '../lib/sendAction.js';
import { useForm } from '../lib/useForm.js';
import { Button, ErrorNotice, Field, Input, Select, Textarea } from './ui.js';
import { Modal } from './Modal.js';
import { useToast } from './Toast.js';

type Channel = 'email' | 'whatsapp';

/** What one channel of a send did. `deepLink` is WhatsApp's own, when nothing left from here. */
interface Delivery {
  signer?: number;
  channel: Channel;
  delivered: boolean;
  error?: string;
  deepLink?: string;
}

export interface SendResult {
  quote: Quote;
  channel: Channel;
  delivered: boolean;
  deepLink?: string;
  link: string;
  /** 1.17 — one entry for each channel the request named, the email first (see `sendTargets`). */
  sends: Delivery[];
}

/** What the copy of a signed document comes back with. */
export interface SendCopyResult {
  document: SignatureDocument;
  link: string;
  sends: Delivery[];
}

/**
 * 1.17 — the two ways one recipient can be reached, in the order they are shown.
 * A phone is WhatsApp: it is the only thing a number is sent on.
 */
const WAYS = [
  { channel: 'whatsapp', use: 'usePhone', field: 'phone', label: 'history.sendByPhone', type: 'tel' },
  { channel: 'email', use: 'useEmail', field: 'email', label: 'history.sendByEmail', type: 'email' },
] as const;

interface Recipient {
  names?: string[] | undefined;
  phones?: string[] | undefined;
  emails?: string[] | undefined;
  name: string;
  phone: string;
  email: string;
}

/**
 * The ways it starts ticked: the ones asked for — unless the recipient has
 * nothing there to send to and has something on the other, which is the better
 * place to start than a box that can only fail.
 */
function startingWays(recipient: Recipient, asked: Channel[]): Channel[] {
  const has = (channel: Channel) => (channel === 'email' ? recipient.email : recipient.phone).trim() !== '';
  if (asked.some(has)) return asked;
  const available = WAYS.map((way) => way.channel).filter(has);
  return available.length > 0 ? available : asked;
}

/** What is posted: the name, and each ticked way with the address beside it. */
function recipientBody(values: Record<string, unknown>, withNotes: boolean, retry: { plan: DeliveryRetryPlan; expectedRevision?: number } | null = null) {
  const selected = WAYS.filter((way) => values[way.use]);
  return {
    ...deliveryRetryFields(retry?.plan ?? null, selected.map((way) => way.channel), retry?.expectedRevision),
    format: String(values.format ?? 'link'),
    recipientName: String(values.recipientName ?? ''),
    channels: selected.map((way) => ({
      channel: way.channel,
      to: String(values[way.field] ?? ''),
    })),
    ...(withNotes ? { message: String(values.message ?? '') } : {}),
  };
}

/**
 * 1.17 — the one popup a quote and a signed document are sent from.
 *
 * It says what is being sent before it asks anything: the customer, which
 * quote or document it is, and what it is about. Then who it is for — a name,
 * and beside it the phone and the email they can be reached on, either or both,
 * each filled in from the card and open to change. What goes out to each of the
 * two is a send of its own.
 */
function SendDialog<R extends { sends: Delivery[]; document?: SignatureDocument }>({
  open,
  title,
  header,
  recipient,
  defaultChannels,
  schema,
  withNotes,
  post,
  sentKey,
  whatsappKey,
  onSent,
  onClose,
}: {
  open: boolean;
  title: string;
  /** The fixed lines at the top: whose it is, which one, and what it is about. */
  header: { customer: string; label: string; subject: string };
  recipient: Recipient;
  defaultChannels: Channel[];
  schema: ZodTypeAny;
  withNotes: boolean;
  post: (body: unknown) => Promise<R>;
  sentKey: string;
  whatsappKey: string;
  onSent: (result: R) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const start = startingWays(recipient, defaultChannels);
  const form = useForm(schema, {
    recipientName: recipient.name,
    phone: recipient.phone,
    email: recipient.email,
    usePhone: start.includes('whatsapp'),
    useEmail: start.includes('email'),
    message: '',
    format: 'link',
  });

  /*
   * What is wrong is read off the same schema the server answers to, once a send
   * has been tried, and read again as the boxes change — so a message goes the
   * moment what it complained about is put right, and not at the next press.
   */
  const [tried, setTried] = useState(false);
  const [retry, setRetry] = useState<{ plan: DeliveryRetryPlan; expectedRevision?: number } | null>(null);
  const checked = tried ? schema.safeParse(recipientBody(form.values, withNotes, retry)) : null;
  const problem = (path: string): string | undefined =>
    checked && !checked.success ? checked.error.issues.find((issue) => issue.path.join('.') === path)?.message : undefined;
  const ticked = WAYS.filter((way) => form.values[way.use]);
  const pickOne = problem('channels');

  const send = useMutation({
    mutationFn: post,
    onSuccess: (result) => {
      const plan = deliveryRetryPlan(result.sends);
      // With the `link` WhatsApp driver nothing is sent server-side; the user
      // finishes the send from their own WhatsApp, so open it for them.
      const handoff = result.sends.find((entry) => entry.channel === 'whatsapp' && !entry.delivered && entry.deepLink);
      if (handoff?.deepLink) {
        void platform.openExternal(handoff.deepLink);
        toast.show(t(whatsappKey));
      } else if (!plan) {
        toast.success(t(sentKey));
      }
      if (plan) {
        toast.error(t('history.partialDelivery'));
        setRetry({ plan, ...(result.document ? { expectedRevision: result.document.contentRevision } : {}) });
        for (const way of WAYS) form.setValue(way.use, plan.channels.includes(way.channel));
        return;
      }
      onSent(result);
      onClose();
    },
  });

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setTried(true);
    void form.submit(
      async (data) => {
        await send.mutateAsync(data);
      },
      (values) => recipientBody(values, withNotes, retry),
    );
  };

  return (
    <Modal
      open={open}
      title={title}
      size="wide"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" loading={form.submitting} onClick={handleSubmit}>
            {t('common.send')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        {form.formError && <ErrorNotice code={form.formError} />}
        {send.data && deliveryRetryPlan(send.data.sends) && <ErrorNotice code="history.partialDelivery" />}

        {/* Fixed text and not a field: it is what the send is about, which is
            not the sender's to change here. */}
        <div className="rounded-lg bg-muted-foreground/10 px-3 py-2.5 dark:bg-muted-foreground/[0.16]">
          <p className="text-sm font-semibold">
            {header.customer || '—'} <span className="font-normal text-muted-foreground">|</span>{' '}
            <span className="ltr-nums font-normal text-muted-foreground">{header.label}</span>
          </p>
          {header.subject && <p className="mt-0.5 text-sm text-muted-foreground">{header.subject}</p>}
        </div>

        <Field label={t('history.sendFormat')} htmlFor="send-format">
          <Select id="send-format" value={String(form.values.format)} onChange={(value) => form.setValue('format', value)}>
            <option value="link">{t('history.formatLink')}</option>
            <option value="pdf">{t('history.formatPdf')}</option>
            <option value="both">{t('history.formatBoth')}</option>
          </Select>
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={t('history.sendRecipient')} htmlFor="send-name">
            <Input
              list="send-names"
              id="send-name"
              value={String(form.values.recipientName ?? '')}
              onChange={(event) => form.setValue('recipientName', event.target.value)}
            />
            <datalist id="send-names">{recipient.names?.map((name) => <option key={name} value={name} />)}</datalist>
          </Field>

          <fieldset className="min-w-0 space-y-1.5">
            <legend className="mb-1.5 block text-xs font-semibold text-muted-foreground">{t('history.sendChannels')}</legend>
            {WAYS.map((way) => {
              const error = problem(`channels.${ticked.indexOf(way)}.to`);
              return (
                <div key={way.channel} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <label className="flex w-36 shrink-0 items-center gap-2 text-sm" htmlFor={`send-use-${way.field}`}>
                      <input
                        id={`send-use-${way.field}`}
                        type="checkbox"
                        disabled={Boolean(retry && !retry.plan.channels.includes(way.channel))}
                        checked={Boolean(form.values[way.use])}
                        onChange={(event) => form.setValue(way.use, event.target.checked)}
                        className="h-4 w-4 shrink-0 rounded border-border accent-primary"
                      />
                      {t(way.label)}
                    </label>
                    <Input
                      list={`send-options-${way.field}`}
                      id={`send-${way.field}`}
                      type={way.type}
                      disabled={Boolean(retry && !retry.plan.channels.includes(way.channel))}
                      dir="ltr"
                      value={String(form.values[way.field] ?? '')}
                      invalid={Boolean(error)}
                      onChange={(event) => {
                        form.setValue(way.field, event.target.value);
                        // Typing an address is choosing to use it.
                        if (event.target.value.trim() && (!retry || retry.plan.channels.includes(way.channel))) form.setValue(way.use, true);
                      }}
                    />
                  </div>
                  <datalist id={`send-options-${way.field}`}>{(way.field === 'phone' ? recipient.phones : recipient.emails)?.map((value) => <option key={value} value={value} />)}</datalist>
                  {error && <span className="block text-xs text-destructive">{t(error)}</span>}
                </div>
              );
            })}
            {pickOne && <span className="block text-xs text-destructive">{t(pickOne)}</span>}
          </fieldset>
        </div>

        {withNotes && (
          <Field label={t('quotes.notes')} htmlFor="send-message" hint={t('common.optional')}>
            <Textarea
              id="send-message"
              value={String(form.values.message ?? '')}
              onChange={(event) => form.setValue('message', event.target.value)}
            />
          </Field>
        )}
      </form>
    </Modal>
  );
}

/**
 * QUO-007 / QUO-008 — a quote, sent or sent again, or copied once approved.
 *
 * Lives here rather than inside the detail screen because the list offers
 * "send again" straight from a row, and both places must send through the same
 * validated payload.
 *
 * 2.4 — what goes is the link, with no choice of sending the PDF instead. The
 * customer has no file of a quote before approving it, and mailing the PDF was
 * the download by another road. A copy of an approved quote still carries the
 * file: the server attaches it to every copy by mail, without being asked.
 */
export function SendQuoteDialog({
  open,
  quote,
  onClose,
  onSent,
  defaultChannels = ['email'],
}: {
  open: boolean;
  quote: Quote;
  onClose: () => void;
  onSent: () => void;
  /** What the editor's channel picker was set to when "send" was pressed. */
  defaultChannels?: Channel[];
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  /*
   * HIST 2.10 — an approved quote is sent as a copy: the same dialog and the
   * same channels, posted to the route that leaves the approval as it is. The
   * send route would refuse it, and rightly — a send asks to be approved.
   */
  const copy = sendActionOf('quote', quote) === 'copy';

  return (
    <SendDialog<SendResult>
      key={`${quote.id}:${open}`}
      open={open}
      title={copy ? t('history.sendCopy') : quote.sentAt ? t('quotes.sendAgain') : t('quotes.sendQuote')}
      header={{
        customer: quote.customer?.name ?? '',
        label: t('quotes.numberBadge', { number: String(quote.number).padStart(2, '0') }),
        subject: quote.title,
      }}
      recipient={{
        names: quote.customer?.names, phones: quote.customer?.phones, emails: quote.customer?.emails,
        name: quote.customer?.name ?? '',
        phone: quote.customer?.phone ?? '',
        email: quote.customer?.email ?? '',
      }}
      defaultChannels={defaultChannels}
      schema={sendQuoteSchema}
      withNotes
      post={(body) => api.post<SendResult>(`/quotes/${quote.id}/${copy ? 'copy' : 'send'}`, body)}
      sentKey={copy ? 'history.copySent' : 'quotes.sentSuccess'}
      whatsappKey="quotes.whatsappReady"
      onSent={() => {
        void queryClient.invalidateQueries({ queryKey: ['quotes'] });
        void queryClient.invalidateQueries({ queryKey: ['history'] });
        void queryClient.invalidateQueries({ queryKey: ['quote', quote.id] });
        void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        onSent();
      }}
      onClose={onClose}
    />
  );
}

/**
 * 1.17 — a copy of a signed document. It used to go in one tap, to whoever the
 * document said and by whichever channel it had an address on, and asked
 * nothing; now it is this popup, with the signer already in it.
 *
 * Only a copy comes through here. Sending a document that is still waiting on
 * a signature stays what it was: the one tap on its row, or the panel's own
 * boxes.
 */
export function SendDocumentCopyDialog({
  open,
  document,
  copy = true,
  onClose,
  onSent,
}: {
  open: boolean;
  document: SignatureDocument;
  copy?: boolean;
  onClose: () => void;
  onSent: (document: SignatureDocument) => void;
}) {
  const { t } = useTranslation();

  return (
    <SendDialog<SendCopyResult>
      key={`${document.id}:${copy}:${open}`}
      open={open}
      title={t(copy ? 'history.sendCopy' : 'quotes.sendAgain')}
      header={{
        customer: document.recipientName,
        // As the lists print it: a document from before numbering has none, and a zero would read as a real one.
        label: t('signing.numberBadge', { number: document.number > 0 ? String(document.number).padStart(2, '0') : '—' }),
        subject: document.title,
      }}
      recipient={{
        names: [...new Set([...(document.signers?.[0]?.names ?? []), ...(document.customer?.names ?? [])])],
        phones: [...new Set([...(document.signers?.[0]?.phones ?? []), ...(document.customer?.phones ?? [])])],
        emails: [...new Set([...(document.signers?.[0]?.emails ?? []), ...(document.customer?.emails ?? [])])],
        name: document.recipientName,
        phone: document.recipientPhone ?? '',
        email: document.recipientEmail ?? '',
      }}
      defaultChannels={[document.recipientEmail ? 'email' : 'whatsapp']}
      schema={sendSignatureSchema}
      withNotes={false}
      post={(body) => api.post<SendCopyResult>(`/signatures/${document.id}/${copy ? 'copy' : 'send'}`, body)}
      sentKey={copy ? 'history.copySent' : 'signing.sentSuccess'}
      whatsappKey="signing.whatsappReady"
      onSent={(result) => onSent(result.document)}
      onClose={onClose}
    />
  );
}
